import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  FileText, Download, Search, Loader2, Users, Car, 
  Calendar, Shield, ArrowLeft, ChevronLeft, ChevronRight,
  Eye, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const AdminAgreements = () => {
  const navigate = useNavigate();
  const { api, user, isPlatformOwner } = useAuth();
  
  const [activeTab, setActiveTab] = useState('host');
  const [loading, setLoading] = useState(true);
  
  // Host agreements state
  const [hostAgreements, setHostAgreements] = useState([]);
  const [hostPage, setHostPage] = useState(1);
  const [hostTotal, setHostTotal] = useState(0);
  const [hostTotalPages, setHostTotalPages] = useState(0);
  const [hostSearch, setHostSearch] = useState('');
  
  // Booking agreements state
  const [bookingAgreements, setBookingAgreements] = useState([]);
  const [bookingPage, setBookingPage] = useState(1);
  const [bookingTotal, setBookingTotal] = useState(0);
  const [bookingTotalPages, setBookingTotalPages] = useState(0);
  const [bookingSearch, setBookingSearch] = useState('');
  
  const LIMIT = 20;

  useEffect(() => {
    if (!isPlatformOwner?.() && !user?.is_platform_owner) {
      toast.error('Admin access required');
      navigate('/');
      return;
    }
    
    fetchHostAgreements();
    fetchBookingAgreements();
  }, []);

  useEffect(() => {
    fetchHostAgreements();
  }, [hostPage]);

  useEffect(() => {
    fetchBookingAgreements();
  }, [bookingPage]);

  const fetchHostAgreements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: hostPage,
        limit: LIMIT
      });
      if (hostSearch) {
        params.append('user_id', hostSearch);
      }
      
      const response = await api.get(`/admin/agreements/hosts?${params}`);
      setHostAgreements(response.data.agreements || []);
      setHostTotal(response.data.total || 0);
      setHostTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Error fetching host agreements:', error);
      toast.error('Failed to load host agreements');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookingAgreements = async () => {
    try {
      const params = new URLSearchParams({
        page: bookingPage,
        limit: LIMIT
      });
      if (bookingSearch) {
        params.append('booking_id', bookingSearch);
      }
      
      const response = await api.get(`/admin/agreements/bookings?${params}`);
      setBookingAgreements(response.data.agreements || []);
      setBookingTotal(response.data.total || 0);
      setBookingTotalPages(response.data.total_pages || 0);
    } catch (error) {
      console.error('Error fetching booking agreements:', error);
      toast.error('Failed to load booking agreements');
    }
  };

  const handleDownload = (url) => {
    if (url) {
      window.open(url, '_blank');
    } else {
      toast.error('Download not available');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short'
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/admin')}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-[#004B8D]" />
              <span className="font-heading font-semibold text-lg">Admin - Agreements</span>
            </div>
          </div>
          <Badge variant="outline">
            Total: {hostTotal + bookingTotal} Agreements
          </Badge>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Host Agreements</p>
                  <p className="text-2xl font-bold text-slate-900">{hostTotal}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Car className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Booking Agreements</p>
                  <p className="text-2xl font-bold text-slate-900">{bookingTotal}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="host" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Host Service Agreements
            </TabsTrigger>
            <TabsTrigger value="booking" className="flex items-center gap-2">
              <Car className="w-4 h-4" />
              Booking Agreements
            </TabsTrigger>
          </TabsList>

          {/* Host Agreements Tab */}
          <TabsContent value="host">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Host Service Agreements</CardTitle>
                    <CardDescription>
                      Agreements between Hosts and HireHub Australia
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by User ID..."
                        value={hostSearch}
                        onChange={(e) => setHostSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchHostAgreements()}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button variant="outline" onClick={fetchHostAgreements}>
                      Search
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  </div>
                ) : hostAgreements.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No host agreements found</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Agreement ID</TableHead>
                          <TableHead>Host</TableHead>
                          <TableHead>Version</TableHead>
                          <TableHead>Accepted At</TableHead>
                          <TableHead>IP Address</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {hostAgreements.map((agreement) => (
                          <TableRow key={agreement.agreement_id}>
                            <TableCell className="font-mono text-xs">
                              {agreement.agreement_id}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{agreement.user_full_name}</p>
                                <p className="text-xs text-slate-500">{agreement.user_email}</p>
                                <p className="text-xs text-slate-400 font-mono">{agreement.user_id}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">v{agreement.agreement_version}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatDate(agreement.accepted_at)}
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">
                              {agreement.ip_address}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(agreement.download_url)}
                                  disabled={!agreement.download_url}
                                  title="Download agreement PDF"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    if (agreement.download_url) {
                                      window.open(agreement.download_url, '_blank', 'noopener,noreferrer');
                                    } else {
                                      toast.error('Agreement PDF not available for viewing');
                                    }
                                  }}
                                  disabled={!agreement.download_url}
                                  title="View agreement"
                                  data-testid={`view-host-agreement-${agreement.agreement_id || agreement.id || agreement.user_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Pagination */}
                    {hostTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <p className="text-sm text-slate-600">
                          Page {hostPage} of {hostTotalPages} ({hostTotal} total)
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setHostPage(p => Math.max(1, p - 1))}
                            disabled={hostPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setHostPage(p => Math.min(hostTotalPages, p + 1))}
                            disabled={hostPage === hostTotalPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Booking Agreements Tab */}
          <TabsContent value="booking">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Booking Agreements</CardTitle>
                    <CardDescription>
                      Rental agreements between Hosts and Renters
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search by Booking ID..."
                        value={bookingSearch}
                        onChange={(e) => setBookingSearch(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && fetchBookingAgreements()}
                        className="pl-10 w-64"
                      />
                    </div>
                    <Button variant="outline" onClick={fetchBookingAgreements}>
                      Search
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {bookingAgreements.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <FileText className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p>No booking agreements found</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Booking ID</TableHead>
                          <TableHead>Host</TableHead>
                          <TableHead>Renter</TableHead>
                          <TableHead>Version</TableHead>
                          <TableHead>Generated At</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookingAgreements.map((agreement) => (
                          <TableRow key={agreement.booking_id}>
                            <TableCell className="font-mono text-xs">
                              <Link 
                                to={`/bookings/${agreement.booking_id}`}
                                className="text-blue-600 hover:underline"
                              >
                                {agreement.booking_id.slice(0, 16)}...
                              </Link>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-sm">{agreement.host_name}</p>
                              <p className="text-xs text-slate-400 font-mono">{agreement.host_user_id?.slice(0, 16)}</p>
                            </TableCell>
                            <TableCell>
                              <p className="font-medium text-sm">{agreement.renter_name}</p>
                              <p className="text-xs text-slate-400 font-mono">{agreement.renter_user_id?.slice(0, 16)}</p>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">v{agreement.agreement_version}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {formatDate(agreement.generated_at)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDownload(agreement.download_url)}
                                  disabled={!agreement.download_url}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/bookings/${agreement.booking_id}`)}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    
                    {/* Pagination */}
                    {bookingTotalPages > 1 && (
                      <div className="flex items-center justify-between mt-4">
                        <p className="text-sm text-slate-600">
                          Page {bookingPage} of {bookingTotalPages} ({bookingTotal} total)
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBookingPage(p => Math.max(1, p - 1))}
                            disabled={bookingPage === 1}
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setBookingPage(p => Math.min(bookingTotalPages, p + 1))}
                            disabled={bookingPage === bookingTotalPages}
                          >
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminAgreements;
